import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Country, State } from 'country-state-city';
import { useTranslation } from '../lib/i18n';

const LocationSelector = ({ onLocationSelect, initialLocation = null }) => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Tüm ülkeleri al
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  // Initial location değiştiğinde state'leri güncelle
  useEffect(() => {
    if (!initialLocation) {
      // Initial location yoksa, state'leri sıfırla
      setSelectedCountry('');
      setSelectedState('');
      setSearchQuery('');
      setStates([]);
      return;
    }

    const allCountries = Country.getAllCountries();
    
    if (initialLocation.country) {
      const country = allCountries.find(c => c.isoCode === initialLocation.country);

      if (country) {
        setSelectedCountry(country.isoCode);
        setSearchQuery(country.name); // Ülke adını search field'a set et
        const countryStates = State.getStatesOfCountry(country.isoCode);
        setStates(countryStates);

        if (initialLocation.state) {
          // State'i önce ISO code ile ara, sonra isim ile ara
          let state = countryStates.find(s => s.isoCode === initialLocation.state);
          if (!state) {
            state = countryStates.find(s => s.name === initialLocation.state);
          }

          if (state) {
            setSelectedState(state.isoCode);
          } else {
            setSelectedState('');
          }
        } else {
          setSelectedState('');
        }
      } else {
        // Ülke bulunamazsa sıfırla
        setSelectedCountry('');
        setSelectedState('');
        setSearchQuery('');
        setStates([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation?.country, initialLocation?.state]);

  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setSelectedState('');

    const countryStates = State.getStatesOfCountry(countryCode);
    setStates(countryStates);
  };

  const handleStateChange = (stateCode) => {
    setSelectedState(stateCode);

    // Lokasyon seçildiğinde callback'i çağır
    if (onLocationSelect) {
      const country = countries.find(c => c.isoCode === selectedCountry);
      const state = states.find(s => s.isoCode === stateCode);

      onLocationSelect({
        country: selectedCountry,
        state: state?.name || stateCode, // State adını gönder, ISO code değil
        countryName: country?.name || '',
        stateName: state?.name || ''
      });
    }
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-2">
        <MapPin className="h-5 w-5 text-blue-600" />
        <span className="font-medium text-gray-700">{t('locationSelector.title')}</span>
      </div>

      {/* Ülke Seçimi */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('locationSelector.country')} *
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={t('locationSelector.searchCountry')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onFocus={() => setShowDropdown(true)}
          />
          {showDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {filteredCountries.map((country) => (
                <button
                  key={country.isoCode}
                  onClick={() => {
                    handleCountryChange(country.isoCode);
                    setSearchQuery(country.name);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{country.flag}</span>
                    <span>{country.name}</span>
                    <span className="text-xs text-gray-500">({country.isoCode})</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* İl/Eyalet Seçimi */}
      {selectedCountry && states.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('locationSelector.state')} {states.length > 0 ? `(${states.length} ${t('locationSelector.found')})` : ''}
          </label>
          <select
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('locationSelector.selectState')}</option>
            {states.map((state) => (
              <option key={state.isoCode} value={state.isoCode}>
                {state.name} {state.isoCode ? `(${state.isoCode})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Seçilen Lokasyon Bilgisi */}
      {selectedCountry && selectedState && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-800">{t('locationSelector.selectedLocation')}:</span>
          </div>
          <p className="text-sm text-blue-700 mt-1">
            {states.find(s => s.isoCode === selectedState)?.name}, {countries.find(c => c.isoCode === selectedCountry)?.name}
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
